import * as React from 'react'
import { Icon } from '../icon'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'

import { Button, buttonVariants } from '../button'
import { mtc } from '../../../utils/style-helpers'
import {
  rootStyles,
  contentStyles,
  itemStyles,
  linkStyles,
  ellipsisStyles,
} from './styles'

const Pagination = ({ className, ...props }: React.ComponentProps<'nav'>) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={mtc(rootStyles(), className)}
    {...props}
  />
)
Pagination.displayName = 'Pagination'

const PaginationContent = ({
  className,
  ...props
}: React.ComponentProps<'ul'>) => (
  <ul className={mtc(contentStyles(), className)} {...props} />
)
PaginationContent.displayName = 'PaginationContent'

const PaginationItem = ({
  className,
  ...props
}: React.ComponentProps<'li'>) => (
  <li className={mtc(itemStyles(), className)} {...props} />
)
PaginationItem.displayName = 'PaginationItem'

type PaginationLinkProps = {
  isActive?: boolean
  disabled?: boolean
  size?: 'sm' | 'md' | 'lg'
  className?: string
  children?: React.ReactNode
  href?: string
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
}

const PaginationLink = ({
  className,
  isActive,
  disabled,
  size = 'sm',
  children,
  onClick,
  ...props
}: PaginationLinkProps) => (
  <Button
    aria-label="pagination link"
    aria-current={isActive ? 'page' : undefined}
    disabled={disabled}
    onClick={onClick}
    className={mtc(
      buttonVariants({
        variant: isActive ? 'pagination' : 'ghost',
        size,
      }),
      className
    )}
    {...props}
  >
    {children || <span className="sr-only">Pagination link</span>}
  </Button>
)
PaginationLink.displayName = 'PaginationLink'

const PaginationPrevious = ({
  className,
  onClick,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to previous page"
    size="sm"
    onClick={onClick}
    className={mtc(linkStyles({ direction: 'previous' }), className)}
    {...props}
  >
    <Icon icon={solid('chevron-left')} className="h-4 w-4" />
    <span>Previous</span>
  </PaginationLink>
)
PaginationPrevious.displayName = 'PaginationPrevious'

const PaginationNext = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to next page"
    size="sm"
    className={mtc(linkStyles({ direction: 'next' }), className)}
    {...props}
  >
    <span>Next</span>
    <Icon icon={solid('chevron-right')} className="h-4 w-4" />
  </PaginationLink>
)
PaginationNext.displayName = 'PaginationNext'

const PaginationEllipsis = ({
  className,
  ...props
}: React.ComponentProps<'span'>) => (
  <span aria-hidden className={mtc(ellipsisStyles(), className)} {...props}>
    <Icon icon={solid('ellipsis')} className="h-4 w-4" />
    <span className="sr-only">More pages</span>
  </span>
)
PaginationEllipsis.displayName = 'PaginationEllipsis'

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
}
